// ---------------------------------------------------------------------------
// Applying an AI analysis to the offering — Apply OVERWRITES the manual field
//
// MAJOR bug (Q2 / NDI-143): with a manual draft already in the offering, click
// Generate → Apply left the manual text in place and the generated content
// vanished. A user who clicked Apply and moved on then ran the campaign on the
// offering they meant to replace — a trust problem, not just an annoyance.
//
// Confirmed product decision (Ferenc, 21 Aug): Apply overwrites the manual
// name and description WHOLESALE, every time. The manual field is only for
// EXTENDING the AI offering afterwards, not for preserving a standalone version.
// The old code kept `offering.name`/`offering.description` whenever they were
// non-empty — which is exactly the silent-keep this fix removes.
// ---------------------------------------------------------------------------

import type { AnalysisResult, OfferingConfig } from './wizard/types.js';

type OfferingText = Pick<OfferingConfig, 'name' | 'description' | 'talkingPoints'>;

/**
 * The name / description / talking-points an approved AI analysis produces on a
 * given offering. Name and description are taken from the result unconditionally
 * (the overwrite the decision requires). Talking points ACCUMULATE — the result's
 * new points are appended (case-insensitively de-duplicated) rather than
 * replacing the list, so Apply never discards the AI's points and a second
 * document's analysis adds to the first instead of wiping it. That accumulation
 * is visible in the points list, so it is not a silent keep.
 */
export function applyAnalysisToOffering(offering: OfferingText, result: AnalysisResult): OfferingText {
  const existing = new Set(offering.talkingPoints.map(p => p.toLowerCase().trim()));
  const newPoints = result.talkingPoints.filter(p => !existing.has(p.toLowerCase().trim()));
  return {
    name: result.productName,
    description: result.productDescription,
    talkingPoints: [...offering.talkingPoints, ...newPoints],
  };
}
